/**
 * Reporting failures instead of swallowing them.
 *
 * Commands ran unguarded, and the page had no handler for errors nothing else
 * caught, so a failure did nothing visible: choosing a recent file that had
 * been deleted simply did nothing, and nothing reached the log either.
 */
import { showNotice } from '../stores/ui'

/** An error as a person should read it, without the IPC wrapping. */
export function describeError(err: unknown): string {
  const text = err instanceof Error ? err.message : String(err)
  const unwrapped = text.replace(/^Error invoking remote method '[^']+': (Error: )?/, '')
  // Web: the platform's own errors carry a code first (`EACCES: …`) for the
  // explanations that look for one; a person reads the words after it.
  const fromPlatform =
    err instanceof Error && /^(PlatformError|PermissionNeededError)$/.test(err.name)
  if (!fromPlatform) return unwrapped
  const words = unwrapped.replace(/^E[A-Z]{3,}: /, '')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Into the page's own error log (web: kept in the page, never sent anywhere). */
export function logError(context: string, err: unknown): void {
  // Web: Firefox's stack, unlike Chromium's, leaves out the message, so the
  // message goes first unless the stack already starts with it.
  const stack = err instanceof Error ? (err.stack ?? '') : ''
  const detail =
    err instanceof Error
      ? stack.includes(err.message)
        ? stack
        : `${err.name}: ${err.message}${stack ? `\n${stack}` : ''}`
      : String(err)
  try {
    window.api.app.logError(`${context}: ${detail}`.slice(0, 4000))
  } catch {
    // Logging must never be what fails.
  }
}

/** A failed action the user took: told on screen, and logged. */
export function reportError(context: string, err: unknown): void {
  showNotice(describeError(err), 'error')
  logError(context, err)
}
